import type { FlowDefinition, FlowStep } from "./types";
import { FLOW_SCHEMA_VERSION } from "./types";

export interface FlowValidationIssue {
  path: string;
  code: string;
  message: string;
  level: "error" | "warning";
}

export interface FlowValidationReport {
  ok: boolean;
  errors: FlowValidationIssue[];
  warnings: FlowValidationIssue[];
}

function walkSteps(steps: FlowStep[]): FlowStep[] {
  return [...steps].sort((a, b) => a.order - b.order);
}

export function validateFlowDefinition(def: FlowDefinition): FlowValidationReport {
  const errors: FlowValidationIssue[] = [];
  const warnings: FlowValidationIssue[] = [];

  if (def.schemaVersion !== FLOW_SCHEMA_VERSION) {
    errors.push({ path: "schemaVersion", code: "SCHEMA_VERSION", message: "schemaVersion inválido", level: "error" });
  }
  if (!def.name?.trim()) errors.push({ path: "name", code: "NAME_REQUIRED", message: "Nombre requerido", level: "error" });
  if (!def.slug?.trim()) errors.push({ path: "slug", code: "SLUG_REQUIRED", message: "Slug requerido", level: "error" });
  if (!def.graph?.steps?.length) {
    errors.push({ path: "graph.steps", code: "STEPS_REQUIRED", message: "Al menos un step", level: "error" });
    return { ok: false, errors, warnings };
  }

  const steps = walkSteps(def.graph.steps);
  const byId = new Map(steps.map((s) => [s.id, s]));
  const keys = new Set<string>();

  if (!byId.has(def.graph.entryStepId)) {
    errors.push({ path: "graph.entryStepId", code: "ENTRY_MISSING", message: "entryStepId inexistente", level: "error" });
  }

  for (const s of steps) {
    if (keys.has(s.key)) {
      errors.push({ path: `graph.steps.${s.key}`, code: "DUPLICATE_KEY", message: `key duplicada: ${s.key}`, level: "error" });
    }
    keys.add(s.key);
    if (s.parentId && !byId.has(s.parentId)) {
      errors.push({ path: `graph.steps.${s.id}`, code: "PARENT_MISSING", message: "parentId inválido", level: "error" });
    }
    if (s.type === "container" && !s.container) {
      warnings.push({ path: `graph.steps.${s.id}`, code: "CONTAINER_EMPTY", message: "Container sin constraints", level: "warning" });
    }
    for (const bid of s.sourceBindingIds) {
      if (!def.sourceBindings.some((b) => b.id === bid)) {
        errors.push({ path: `graph.steps.${s.id}`, code: "BINDING_MISSING", message: `binding ${bid} ausente`, level: "error" });
      }
    }
  }

  for (const b of def.sourceBindings) {
    if (!byId.has(b.stepId)) {
      errors.push({ path: `sourceBindings.${b.id}`, code: "BINDING_STEP", message: "stepId inválido", level: "error" });
    }
  }

  for (const t of def.graph.transitions ?? []) {
    if (!byId.has(t.fromStepId)) {
      errors.push({ path: `transitions.${t.id}`, code: "TRANSITION_FROM", message: "fromStepId inválido", level: "error" });
    }
    if (t.toStepId !== "__complete__" && !byId.has(t.toStepId)) {
      errors.push({ path: `transitions.${t.id}`, code: "TRANSITION_TO", message: "toStepId inválido", level: "error" });
    }
  }

  // Orphans: steps not entry and not targeted (warning if linear-by-order still works)
  const targeted = new Set((def.graph.transitions ?? []).map((t) => t.toStepId));
  for (const s of steps) {
    if (s.id !== def.graph.entryStepId && !targeted.has(s.id) && (def.graph.transitions?.length ?? 0) > 0) {
      warnings.push({ path: `graph.steps.${s.id}`, code: "ORPHAN_STEP", message: "Step no alcanzable por transitions", level: "warning" });
    }
  }

  return { ok: errors.length === 0, errors, warnings };
}
